import { Link } from '@mui/material';
import { t } from 'i18next';
import { BlankCell } from '../../../common/components/Cells/BlankCell';
import { FilterValueInputType } from '../../../common/components/FilterPanel/enum/FilterValueInputType';
import { Operation as FilterOperation } from '../../../common/components/FilterPanel/enum/Operation';
import { FilterConfiguration } from '../../../common/components/FilterPanel/types';
import { TypographyOverflow } from '../../../common/components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState } from '../../../common/controllers/PaginatedTableController';
import { WithRouterProps, withRouter } from '../../../common/higher-order-components/with-router';
import { OperationAlgorithm, getOperationAlgorithmLabel } from '../../../common/operations/enums/OperationAlgorithm';
import { OperationContext, getOperationContextLabel } from '../../../common/operations/enums/OperationContext';
import { OperationType, getOperationTypeLabel } from '../../../common/operations/enums/OperationType';
import { FieldConfiguration } from '../../../common/types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { DateUtils } from '../../../common/utils/DateUtils';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { PATHS } from '../../../routing/routes';
import { ReportOutletContextType } from '../../common/contexts/ReportOutletContext';
import { Report, ReportDone, ReportFailed, ReportPending } from '../../common/models/Report';
import { Operation } from '../models/Operation';
import { OperationRepository } from '../services/OperationRepository';

type Props = WithRouterProps<ReportOutletContextType> & {};

type State = PaginatedTableState & {};

export class Controller extends PaginatedTableController<Operation, Props, State> {
  private readonly repository = new OperationRepository();

  componentDidMount(): void {
    super.componentDidMount();

    this.loadReportContext();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
    super.componentDidUpdate(prevProps, prevState);

    this.onReportContextChanged(prevProps.context.report, this.props.context.report);
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.repository.cancelAllRequests();
  }

  protected doInitialDataFetch() {
    // nothing, we aren't fetching anything until we receive a ReportDone from context
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<Operation>> {
    const { report } = this.props.context;

    if (!(report instanceof ReportDone) || report.getOperationsCount() === 0) return BLANK_PAGINATION_RESULT;

    try {
      return await this.repository.getList(report.getId(), cursorInfo, this.getFilters(), this.getSorters());
    } catch (e: unknown) {
      this.handleError(e);
      return BLANK_PAGINATION_RESULT;
    }
  }

  protected getFieldConfigurations(): FieldConfiguration<Operation>[] {
    const { report } = this.props.context;
    return [
      {
        fieldKey: 'id',
        label: t('id'),
        description: t('id'),
        columnProps: { maxWidth: 100 },
        sortable: true,
        gqlKeys: { sorterField: 'id' },
        render: (item, { tabIndex }) => (
          <Link
            tabIndex={tabIndex}
            href={UrlHelper.generatePath(PATHS.REPORT_OPERATION, { reportId: report?.getId().toString(), operationId: item.getId().toString() })}
            aria-label={t('a11y.report.operations.idLabel', { operationId: item.getId() }) as string}
          >
            {item.getId()}
          </Link>
        ),
      },
      {
        fieldKey: 'type',
        label: t('type'),
        description: t('type'),
        sortable: true,
        gqlKeys: { sorterField: 'type' },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{getOperationTypeLabel(item.getType())}</TypographyOverflow>,
      },
      {
        fieldKey: 'context',
        label: t('context'),
        description: t('context'),
        sortable: true,
        gqlKeys: { sorterField: 'context' },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{getOperationContextLabel(item.getContext())}</TypographyOverflow>,
      },
      {
        fieldKey: 'algorithms',
        label: t('algorithms'),
        description: t('algorithms'),
        render: (item, { hasFocus }) => {
          const algorithms = item.getAlgorithms();
          if (algorithms.length > 0) {
            return <TypographyOverflow hasFocus={hasFocus}>{algorithms.join(', ')}</TypographyOverflow>;
          }
          return <BlankCell blankText={t('none')} />;
        },
      },
      {
        fieldKey: 'timestamp',
        label: t('timestamp'),
        description: t('timestamp'),
        sortable: true,
        gqlKeys: { sorterField: 'timestamp' },
        columnProps: { minWidth: 250 },
        render: (item, { hasFocus }) => {
          const timestamp = item.getTimestamp();
          if (!timestamp) {
            return <BlankCell blankText={t('unknown')} />;
          }
          return <TypographyOverflow hasFocus={hasFocus}>{DateUtils.toTimestampString(timestamp)}</TypographyOverflow>;
        },
      },
    ];
  }

  protected shouldStoreFiltersInUrl(): boolean {
    return true;
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [
      {
        filterKey: 'type',
        label: t('type'),
        type: FilterValueInputType.SELECT,
        options: Object.values(OperationType).map((value) => ({ value, label: getOperationTypeLabel(value) })),
        operations: [
          { value: FilterOperation.EQ, label: t('filterLabels.is') },
          { value: FilterOperation.NEQ, label: t('filterLabels.isNot') },
        ],
      },
      {
        filterKey: 'context',
        label: t('context'),
        type: FilterValueInputType.SELECT,
        options: Object.values(OperationContext).map((value) => ({ value, label: getOperationContextLabel(value) })),
        operations: [
          { value: FilterOperation.EQ, label: t('filterLabels.is') },
          { value: FilterOperation.NEQ, label: t('filterLabels.isNot') },
        ],
      },
      {
        filterKey: 'algorithms',
        label: t('algorithms'),
        type: FilterValueInputType.SELECT,
        options: Object.values(OperationAlgorithm)
          .map((value) => ({ value, label: getOperationAlgorithmLabel(value) }))
          .sort((a, b) => a.label.localeCompare(b.label)),
        operations: [{ value: FilterOperation.CONTAINS, label: t('filterLabels.contains') }],
      },
      {
        filterKey: 'timestamp',
        label: t('timestamp'),
        type: FilterValueInputType.TIMESTAMP,
        operations: [
          { value: FilterOperation.LT, label: t('filterLabels.isBefore') },
          { value: FilterOperation.LE, label: t('filterLabels.isBeforeOrEqual') },
          { value: FilterOperation.EQ, label: t('filterLabels.is') },
          { value: FilterOperation.GE, label: t('filterLabels.isAfterOrEqual') },
          { value: FilterOperation.GT, label: t('filterLabels.isAfter') },
        ],
      },
    ];
  }

  private loadReportContext() {
    // When the component mounts, consider that the previous report is null
    this.onReportContextChanged(null, this.props.context.report);
  }

  private onReportContextChanged(prevReport: Report | null, currReport: Report | null) {
    // Report hasn't changed, do nothing
    if (prevReport === currReport) return;

    if (prevReport !== null && prevReport.getId() !== currReport?.getId()) {
      // Report has changed (e.g. through a version selector)
      // Reset the table state (including stored data and pagination) and reload
      this.resetState(() => this.loadReportContext());
      return;
    }

    if (!currReport || currReport instanceof ReportPending) {
      // The report is not ready yet, display the initial loading state
      this.resetState();
    } else if (currReport instanceof ReportFailed) {
      // The report is failed, display the blank loaded state
      this.setupBlankState();
    } else if (currReport instanceof ReportDone) {
      // The report is done, load the operations
      super.doInitialDataFetch();
    }
  }
}

export const ReportOperationsController = withRouter<ReportOutletContextType>(Controller);
